import type { ProviderConfig, VoiceAgentProvider } from "./types.js";

/**
 * Provider registry.
 *
 * Providers self-register so the widget can resolve a `provider="…"` attribute
 * to an adapter without the core importing any provider package. Adding a
 * provider = shipping a new provider package that calls
 * {@link registerProvider} — no change to core, widget, embed, or React.
 */
export type VoiceAgentProviderFactory<TConfig = ProviderConfig> = () => VoiceAgentProvider<TConfig>;

const registry = new Map<string, VoiceAgentProviderFactory>();

/** Notified when any provider registers. Registration order is not guaranteed. */
export type ProviderRegistrationListener = (name: string) => void;
const listeners = new Set<ProviderRegistrationListener>();

/**
 * Observe provider registrations.
 *
 * A declaratively embedded widget can be parsed before the `<script>` that
 * self-registers its provider has executed — script order is not guaranteed for
 * `async` tags. Consumers use this to wait for a named provider rather than
 * failing on a race they cannot control. Returns an idempotent unsubscribe.
 *
 * The listener fires only for registrations that happen **after** subscribing —
 * it is not replayed for providers already in the registry. Subscribing without
 * first checking {@link hasProvider} therefore waits forever when the provider
 * arrived first, which is the common case for a bundled provider:
 *
 * ```ts
 * if (!hasProvider("acme")) onProviderRegistered((name) => { if (name === "acme") start(); });
 * else start();
 * ```
 *
 * Subscriptions are keyed by function reference, like `addEventListener`:
 * registering the same reference twice yields one subscription (it is called
 * once per registration), and either returned unsubscribe removes it. Two
 * separately-created closures with identical bodies are two subscriptions.
 */
export function onProviderRegistered(listener: ProviderRegistrationListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Register the factory for a provider name. A later registration replaces the
 * earlier factory. Every widget controller receives a fresh adapter instance.
 */
export function registerProvider<TConfig = ProviderConfig>(
  name: string,
  factory: VoiceAgentProviderFactory<TConfig>,
): void {
  registry.set(name, factory as unknown as VoiceAgentProviderFactory);

  notifyProviderRegistered(name);
}

function notifyProviderRegistered(name: string): void {
  // Copy first: a listener may unsubscribe itself (the common case - it was
  // waiting for exactly this provider). A listener fault must not prevent
  // registration or starve the listeners after it.
  for (const listener of [...listeners]) {
    try {
      listener(name);
    } catch (listenerError) {
      // Report every throw, unlike the per-message onRawEvent notification (which latches
      // to first-throw-only to avoid flooding the console): registration is not a hot
      // path — it fires a handful of times per page, so there is nothing to flood. A
      // latch would also be un-resettable module-level state on top of state the test
      // suite already carefully cleans up per-test; that would regress testability.
      // Staying fully silent leaves the consumer with a broken observer and no signal.
      console.error(
        "[voice-agent] onProviderRegistered listener threw; it is isolated from registration.",
        listenerError,
      );
    }
  }
}

/** Create the provider instance owned by one `createVoiceAgent` controller. */
export function createProvider(name: string): VoiceAgentProvider | undefined {
  const provider = registry.get(name)?.();
  if (provider && provider.name !== name) {
    throw new Error(
      `provider factory registered as "${name}" created provider "${provider.name}".`,
    );
  }
  return provider;
}

/** Whether a provider with this name has been registered. */
export function hasProvider(name: string): boolean {
  return registry.has(name);
}

/** Names of all registered providers. */
export function listProviders(): string[] {
  return [...registry.keys()];
}

/** Remove a provider registration. Returns `true` if one was removed. Primarily for tests. */
export function unregisterProvider(name: string): boolean {
  return registry.delete(name);
}
